import React, { useState, useEffect, useRef } from 'react';
import WebShell from '../web/WebShell';
import { getHashPath, setHashPath, subscribeHash } from '../web/webRoute';

type NavParams = Record<string, unknown>;

type Nav = {
  navigate: (screen: string, params?: NavParams) => void;
  replace: (screen: string, params?: NavParams) => void;
  goBack: () => void;
  canGoBack: () => boolean;
};

// Web entry. Authentication-gated:
//  - Unauthed → Welcome / Login / Signup (still RN-Web mobile screens)
//  - Authed   → the new web shell at src/web/* (plain React, matches the
//               Claude Design "Kidly Web" prototype with top nav).
// Note: AddChild / PairTV / Subscription / ContentAdmin etc. are still
// available as RN-Web screens so the profile menu can navigate to them.
// Outer screens ↔ URL-hash map. WebShell / ParentHome are intentionally
// absent — the shell owns the hash via its active tab (see WebShell +
// src/web/webRoute.ts). Child/native-only screens are omitted too.
const SCREEN_TO_PATH: Record<string, string> = {
  Welcome: '/welcome',
  Login: '/login',
  Signup: '/signup',
  ForgotPassword: '/forgot-password',
  AddChild: '/add-child',
  EditChild: '/edit-child',
  Subscription: '/subscription',
  PinSetup: '/pin-setup',
  ContentAdmin: '/content-admin',
  PairTV: '/pair-tv',
};
const PATH_TO_SCREEN: Record<string, string> = Object.fromEntries(
  Object.entries(SCREEN_TO_PATH).map(([s, p]) => [p, s]),
);

export default function WebApp() {
  const [screen, setScreen] = useState<string>('Welcome');
  const [hydrated, setHydrated] = useState(false);

  const [params, setParams] = useState<NavParams>({});
  const screenSynced = useRef(false);

  useEffect(() => {
    // Web is browse-first: everyone — logged in or not — lands in the shell.
    // A logged-OUT visitor sees the guest variant (free videos, Sign in /
    // Sign up in the topbar) instead of being forced through an auth wall or
    // the mobile-style intro carousel. Login / Signup are still reachable
    // screens (from the topbar and deep links).
    let initialScreen = 'WebShell';
    let initialParams: NavParams = {};

    // Account-only deep links (add a child, pair a TV) still need auth, so
    // detect it to gate those — even though the default landing is the shell.
    let authed = false;
    try {
      const raw = localStorage.getItem('mapa-auth');
      authed = !!(raw && JSON.parse(raw)?.state?.isAuthenticated);
    } catch {}

    // Deep-link entry: the TV's QR codes encode URLs with `?action=`
    // params. When a phone scans them and opens the parent web, route
    // straight to the right screen instead of dumping them on Home.
    try {
      if (typeof window !== 'undefined' && window.location?.search) {
        const q = new URLSearchParams(window.location.search);
        const action = q.get('action');
        if (action === 'add-child') {
          if (authed) {
            initialScreen = 'AddChild';
          } else {
            initialScreen = 'Login';
            initialParams = { next: 'AddChild' };
          }
        } else if (action === 'pair') {
          const code = q.get('code') ?? '';
          if (authed) {
            initialScreen = 'PairTV';
            initialParams = { prefillCode: code };
          } else {
            initialScreen = 'Login';
            initialParams = { next: 'PairTV', prefillCode: code };
          }
        }
        // Strip params from URL once consumed so refresh doesn't re-trigger.
        if (action) {
          window.history.replaceState({}, '', window.location.pathname);
        }
      }
    } catch {}

    // No QR/action deep-link consumed → honor the URL hash, so a refresh on
    // e.g. #/login or #/pair-tv restores that screen. Auth-only screens fall
    // back to the shell when not signed in.
    try {
      if (initialScreen === 'WebShell') {
        const s = PATH_TO_SCREEN[getHashPath()];
        if (s) {
          const publicScreen = [
            'Welcome',
            'Login',
            'Signup',
            'ForgotPassword',
          ].includes(s);
          if (publicScreen || authed) initialScreen = s;
        }
      }
    } catch {}

    setScreen(initialScreen);
    setParams(initialParams);
    setHydrated(true);
  }, []);

  // screen → hash for outer screens (the shell manages its own tab hash).
  // Skip the first run so the mount-adopt above isn't clobbered.
  useEffect(() => {
    if (!screenSynced.current) {
      screenSynced.current = true;
      return;
    }
    const path = SCREEN_TO_PATH[screen];
    if (path) setHashPath(path);
  }, [screen]);

  // hash → screen (browser back / forward). A non-outer hash (a shell tab
  // like #/activity, or the bare /) means "show the shell".
  useEffect(
    () =>
      subscribeHash(() => {
        const s = PATH_TO_SCREEN[getHashPath()];
        if (s) {
          setScreen((cur) => (cur !== s ? s : cur));
        } else {
          setScreen((cur) =>
            cur === 'WebShell' || cur === 'ParentHome' ? cur : 'WebShell',
          );
        }
      }),
    [],
  );

  const [history, setHistory] = useState<{ screen: string; params: NavParams }[]>([]);

  const nav: Nav = {
    navigate: (s, p = {}) => {
      setHistory((h) => [...h, { screen, params }]);
      setScreen(s);
      setParams(p);
    },
    replace: (s, p = {}) => {
      setScreen(s);
      setParams(p);
    },
    goBack: () => {
      const prev = history[history.length - 1];
      if (prev) {
        setScreen(prev.screen);
        setParams(prev.params);
        setHistory((h) => h.slice(0, -1));
      }
    },
    canGoBack: () => history.length > 0,
  };

  const route = { params } as any;

  if (!hydrated) return null;

  // Welcome / Login / Signup flows post-login transition to WebShell.
  // Hook the legacy ParentHome route to WebShell so any nav.replace from
  // those screens still lands on the new web app.
  if (screen === 'ParentHome' || screen === 'WebShell') {
    return (
      <div className="mw-root">
        <WebShell nav={nav} />
      </div>
    );
  }

  switch (screen) {
    case 'Welcome': { const S = require('../screens/parent/WelcomeScreen').default; return <S navigation={nav} route={route} />; }
    case 'Login': { const S = require('../screens/parent/LoginScreen').default; return <S navigation={nav} route={route} />; }
    case 'Signup': { const S = require('../screens/parent/SignupScreen').default; return <S navigation={nav} route={route} />; }
    case 'ForgotPassword': { const S = require('../screens/parent/ForgotPasswordScreen').default; return <S navigation={nav} route={route} />; }
    case 'AddChild': { const S = require('../screens/parent/AddChildScreen').default; return <S navigation={nav} route={route} />; }
    case 'EditChild': { const S = require('../screens/parent/EditChildScreen').default; return <S navigation={nav} route={route} />; }
    case 'Controls': { const S = require('../screens/parent/ControlsScreen').default; return <S navigation={nav} route={route} />; }
    case 'Dashboard': { const S = require('../screens/parent/DashboardScreen').default; return <S navigation={nav} route={route} />; }
    case 'Subscription': { const S = require('../screens/parent/SubscriptionScreen').default; return <S navigation={nav} route={route} />; }
    case 'PinSetup': { const S = require('../screens/parent/PinSetupScreen').default; return <S navigation={nav} route={route} />; }
    case 'ContentAdmin': { const S = require('../screens/parent/ContentAdminScreen').default; return <S navigation={nav} route={route} />; }
    case 'PairTV': { const S = require('../screens/parent/PairTvScreen').default; return <S navigation={nav} route={route} />; }
    case 'ChildHome': { const S = require('../screens/child/ChildHomeScreen').default; return <S navigation={nav} route={route} />; }
    case 'Library': { const S = require('../screens/child/LibraryScreen').default; return <S navigation={nav} route={route} />; }
    case 'Player': { const S = require('../screens/child/PlayerScreen').default; return <S navigation={nav} route={route} />; }
    default: return null;
  }
}
